/**
 * AssignConfirmDialog — shared by the single-row assign and the bulk bar.
 * This is an INSTANT server write that rewrites live config, so it always
 * confirms, names both sets, and calls out a STANDARD → Custom flip.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';

import { ConfirmDialog } from '@/components/ui/layout/ConfirmDialog';

export interface AssignConfirmDialogProps {
  open: boolean;
  packageName: string;
  formulaSetName?: string;
  ruleSetName?: string;
  /** Single assign: the campaign's name. Bulk: undefined + count > 1. */
  campaignName?: string;
  /** Bulk target count; 1 = single-row assign. */
  count?: number;
  /** True when at least one target is in STANDARD mode. */
  flipsStandard?: boolean;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const AssignConfirmDialog: FC<AssignConfirmDialogProps> = ({
  open,
  packageName,
  formulaSetName,
  ruleSetName,
  campaignName,
  count = 1,
  flipsStandard = false,
  busy,
  onConfirm,
  onCancel,
}) => {
  const targetText =
    count > 1 ? `${count} selected flows` : `"${campaignName ?? ''}"`;
  return (
    <ConfirmDialog
      open={open}
      title="Apply package now?"
      description={
        <span className="space-y-1.5">
          <span className="block">
            <span className="font-medium">“{packageName}”</span>
            {formulaSetName || ruleSetName ? (
              <span className="text-muted-foreground">
                {' '}— Formula: {formulaSetName ?? '—'} · Rules: {ruleSetName ?? '—'}
              </span>
            ) : null}
          </span>
          <span className="block">
            This saves the configuration for {targetText} immediately and the
            next scheduler run uses it.
          </span>
          {flipsStandard ? (
            <span className="block text-warning">
              {count > 1 ? 'Standard-mode campaigns switch' : 'This campaign is in Standard mode — applying switches it'}{' '}
              to Custom.
            </span>
          ) : null}
        </span>
      }
      confirmLabel={count > 1 ? `Apply to ${count}` : 'Apply now'}
      busy={busy}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
};

export default AssignConfirmDialog;
